import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/**
 * LYNE purchase panel — the Peekaboo Pet Tote panel, generalised for every product page.
 *
 * Owns colour/size/offer selection and add-to-cart. The theme's own variant-picker stays in the
 * DOM (visually hidden, inert) and is driven from here, so the gallery, price block, `?variant=`
 * deep links and sold-out states behave exactly as they always have.
 *
 * Offers: `none` (one item), `pair` (one or two, the second saving comes from a server-side
 * automatic discount), `box3` (always three, one size, buy-2-get-1 discount) and `tier3` (one,
 * two or three, each piece its own colour and size; min-2 and min-3 automatic discounts). Nothing here
 * changes what is charged; the figures shown only preview it.
 */
class LyneBuy extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    const data = JSON.parse(this.querySelector('[data-lyne-buy-data]')?.textContent || '{}');
    this.variants = data.variants || [];
    this.colourIndex = data.colourIndex ?? -1;
    this.sizeIndex = data.sizeIndex ?? -1;
    this.totals = data.totals || {};
    this.offer = this.dataset.offer || 'none';
    this.noun = this.dataset.noun || 'piece';
    this.nouns = this.dataset.nouns || 'pieces';

    this.section = this.closest('.shopify-section');
    this.bar = document.querySelector(`lyne-buy-bar[data-for="${this.id}"]`);

    this.colourButtons = Array.from(this.querySelectorAll('[data-colour]'));
    this.sizeButtons = Array.from(this.querySelectorAll('[data-size]'));
    this.offerButtons = Array.from(this.querySelectorAll('button[data-qty]'));
    this.extrasPanel = this.querySelector('[data-extras]');
    this.extras = Array.from(this.querySelectorAll('[data-extra]')).map((row) => ({
      row,
      colour: row.querySelector('[data-extra-colour]'),
      size: row.querySelector('[data-extra-size]'),
      touched: false,
    }));
    this.addButton = this.querySelector('[data-add]');
    this.summary = this.querySelector('[data-summary]');
    this.colourName = this.querySelector('[data-colour-name]');

    // Sizes that exist at all. A product with a single size never asks for one.
    this.sizeValues = this.sizeIndex >= 0 ? [...new Set(this.variants.map((v) => v.options[this.sizeIndex]))] : [];
    this.needsSize = this.sizeValues.length > 1;

    const selected = this.variants.find((variant) => variant.id === data.selectedId) || this.variants[0];
    this.colour = this.colourIndex >= 0 ? selected?.options[this.colourIndex] : null;

    // A size is only pre-selected when the shopper arrived on an explicit variant URL.
    const deepLinked = this.#variantFromUrl();
    this.size = this.needsSize ? (deepLinked ? deepLinked.options[this.sizeIndex] : null) : this.sizeValues[0] ?? null;
    if (deepLinked && this.colourIndex >= 0) this.colour = deepLinked.options[this.colourIndex];

    this.qty = this.offer === 'box3' ? 3 : 1;

    for (const button of this.colourButtons) {
      button.addEventListener('click', () => this.#chooseColour(button.dataset.colour));
      button.addEventListener('keydown', (event) => this.#radioKeys(event, this.colourButtons));
    }
    for (const button of this.sizeButtons) {
      button.addEventListener('click', () => this.#chooseSize(button.dataset.size));
      button.addEventListener('keydown', (event) => this.#radioKeys(event, this.sizeButtons));
    }
    for (const button of this.offerButtons) {
      button.addEventListener('click', () => this.#chooseQty(Number(button.dataset.qty)));
      button.addEventListener('keydown', (event) => this.#radioKeys(event, this.offerButtons));
    }
    for (const extra of this.extras) {
      if (extra.colour && this.colour) extra.colour.value = this.colour;
      extra.colour?.addEventListener('change', () => {
        extra.touched = true;
        this.refresh();
      });
      extra.size?.addEventListener('change', () => this.refresh());
    }
    this.addButton?.addEventListener('click', () => this.submit());
    this.bar?.querySelector('[data-bar-add]')?.addEventListener('click', () => this.submit());
    this.querySelector('[data-fit-open]')?.addEventListener('click', () => this.#openFitGuide());

    // Keep in step if anything else on the page changes the variant.
    this.section?.addEventListener(StandardEvents.productSelect, (event) => {
      event.promise
        ?.then((result) => {
          const id = Number(result?.detail?.variantId);
          const known = this.variants.find((variant) => variant.id === id);
          if (known && this.colourIndex >= 0) {
            this.colour = known.options[this.colourIndex];
            this.refresh();
          }
        })
        .catch(() => {})
        .finally(() => this.#sealThemePicker());
    });

    this.#sealThemePicker();
    this.#watchStickyBar();
    this.refresh();
  }

  /** @returns {object | undefined} the variant named by `?variant=`, when it is one of ours */
  #variantFromUrl() {
    const id = Number(new URLSearchParams(window.location.search).get('variant'));
    return this.variants.find((variant) => variant.id === id);
  }

  /** Roving arrow-key movement inside a group of role="radio" buttons. */
  #radioKeys(event, group) {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    const step = keys[event.key];
    if (!step) return;
    event.preventDefault();
    const usable = group.filter((button) => !button.disabled);
    const index = usable.indexOf(event.currentTarget);
    const next = usable[(index + step + usable.length) % usable.length];
    next?.focus();
    next?.click();
  }

  #chooseColour(value) {
    if (!value || value === this.colour) return;
    this.colour = value;
    // Extra pieces follow the first until the shopper picks something else for them.
    for (const extra of this.extras) if (extra.colour && !extra.touched) extra.colour.value = value;
    this.#pushToThemePicker();
    this.refresh();
  }

  #chooseSize(value) {
    const variant = this.findVariant(this.colour, value);
    if (!variant || !variant.available) return;
    this.size = value;
    this.#pushToThemePicker();
    this.refresh();
  }

  #chooseQty(qty) {
    this.qty = qty;
    if (this.extrasPanel) this.extrasPanel.hidden = qty < 2;
    // Tiers show one row per extra piece in the chosen bundle.
    if (this.offer === 'tier3') this.extras.forEach((extra, i) => (extra.row.hidden = i >= qty - 1));
    for (const extra of this.extras) if (extra.colour && !extra.touched) extra.colour.value = this.colour;
    this.refresh();
  }

  /** The theme's picker is a mechanism here, not a control: out of the tab order and the a11y tree. */
  #sealThemePicker() {
    const picker = this.section?.querySelector('variant-picker');
    if (!picker || picker.inert) return;
    picker.inert = true;
    picker.setAttribute('aria-hidden', 'true');
  }

  /** Check the theme's hidden radios and let its variant-picker update gallery, price and URL. */
  #pushToThemePicker() {
    const picker = this.section?.querySelector('variant-picker');
    if (!picker) return;
    this.#sealThemePicker();

    const check = (index, value) => {
      if (index < 0 || value == null) return null;
      const fieldset = picker.querySelector(`fieldset[data-fieldset-index="${index}"]`);
      const input = fieldset?.querySelector(`input[value="${CSS.escape(value)}"]`);
      if (!input || input.checked) return input || null;
      input.checked = true;
      return input;
    };

    const changed = [check(this.colourIndex, this.colour), check(this.sizeIndex, this.size)].filter(Boolean);
    const last = changed[changed.length - 1];
    if (last) last.dispatchEvent(new Event('change', { bubbles: true }));
    setTimeout(() => this.#syncUrl(), 60);
  }

  #syncUrl() {
    const url = new URL(window.location.href);
    const variant = this.size ? this.findVariant(this.colour, this.size) : null;
    if (variant && this.needsSize) url.searchParams.set('variant', String(variant.id));
    else if (this.needsSize) url.searchParams.delete('variant');
    if (url.href !== window.location.href) history.replaceState({}, '', url.toString());
  }

  findVariant(colour, size) {
    return this.variants.find(
      (variant) =>
        (this.colourIndex < 0 || colour == null || variant.options[this.colourIndex] === colour) &&
        (this.sizeIndex < 0 || variant.options[this.sizeIndex] === size)
    );
  }

  #anyVariant(colour) {
    const matches = this.variants.filter((v) => this.colourIndex < 0 || v.options[this.colourIndex] === colour);
    return matches.find((v) => v.available) || matches[0];
  }

  /** Extra pieces in use for the chosen offer, with their resolved colour and size. */
  #extraPicks() {
    const count = this.offer === 'box3' ? 2 : Math.max(0, this.qty - 1);
    return this.extras.slice(0, count).map((extra) => ({
      extra,
      colour: extra.colour ? extra.colour.value : this.colour,
      // Box of 3 is one size for all three; a pair lets each piece have its own.
      size: this.offer === 'box3' ? this.size : extra.size ? extra.size.value : this.size,
    }));
  }

  resolve() {
    const first = this.findVariant(this.colour, this.size);
    if (!this.size && this.needsSize) return { items: [], problem: '', focus: 'size' };
    if (!first) return { items: [], problem: '', focus: 'size' };
    if (!first.available) return { items: [], problem: 'That size is sold out in this colour.', focus: 'size' };

    const picks = [first];
    for (const [i, pick] of this.#extraPicks().entries()) {
      if (!pick.size) return { items: [], problem: '', focus: 'extra', extra: i };
      const variant = this.findVariant(pick.colour, pick.size);
      if (!variant || !variant.available) {
        return {
          items: [],
          problem: `${this.#cap(this.noun)} ${i + 2} is sold out in that colour and size. Please pick another.`,
          focus: 'extra',
          extra: i,
        };
      }
      picks.push(variant);
    }

    const counts = new Map();
    for (const variant of picks) counts.set(variant.id, (counts.get(variant.id) || 0) + 1);
    return { items: Array.from(counts, ([id, quantity]) => ({ id, quantity })), problem: '', focus: '' };
  }

  /** Preview of what the cart will charge for the chosen offer. */
  #offerFigures() {
    const first = this.findVariant(this.colour, this.size) || this.#anyVariant(this.colour);
    if (!first) return null;
    if (this.offer === 'box3') return this.totals[`3:${first.price}`] || null;
    if (this.offer === 'tier3') return this.qty > 1 ? this.totals[`${this.qty}:${first.price}`] || null : null;
    if (this.offer === 'pair') {
      const pick = this.#extraPicks()[0] || { colour: this.colour, size: this.size };
      const second = this.findVariant(pick.colour, pick.size) || this.#anyVariant(pick.colour) || first;
      return this.totals[`2:${first.price + second.price}`] || null;
    }
    return null;
  }

  #cap(word) {
    return word.charAt(0).toUpperCase() + word.slice(1);
  }

  refresh() {
    const single = this.findVariant(this.colour, this.size) || this.#anyVariant(this.colour);
    const figures = this.#offerFigures();

    for (const button of this.colourButtons) {
      const chosen = button.dataset.colour === this.colour;
      button.setAttribute('aria-checked', String(chosen));
      button.tabIndex = chosen ? 0 : -1;
      button.disabled = !this.variants.some(
        (variant) => variant.options[this.colourIndex] === button.dataset.colour && variant.available
      );
    }

    for (const button of this.sizeButtons) {
      const chosen = button.dataset.size === this.size;
      button.setAttribute('aria-checked', String(chosen));
      button.tabIndex = chosen || (!this.size && button === this.sizeButtons[0]) ? 0 : -1;
      const variant = this.findVariant(this.colour, button.dataset.size);
      button.disabled = !variant || !variant.available;
    }

    for (const button of this.offerButtons) {
      const chosen = Number(button.dataset.qty) === this.qty;
      button.setAttribute('aria-checked', String(chosen));
      button.tabIndex = chosen ? 0 : -1;
    }

    if (this.colourName && this.colour) this.colourName.textContent = this.colour;

    const singleTotal = this.querySelector('[data-single-total]');
    if (singleTotal && single) singleTotal.textContent = single.priceMoney;

    const count = this.offer === 'box3' ? 3 : 2;
    if (figures) {
      this.querySelector('[data-offer-total]')?.replaceChildren(figures.total);
      this.querySelector('[data-offer-each]')?.replaceChildren(
        `${figures.each} each${this.colourButtons.length ? ' · mix colours' : ''}`
      );
      this.querySelector('[data-offer-saving]')?.replaceChildren(
        `Save ${figures.saving} against buying ${count} separately`
      );
    }

    // Disable sold-out choices inside each extra piece.
    for (const pick of this.#extraPicks()) {
      if (pick.extra.size) {
        for (const option of Array.from(pick.extra.size.options)) {
          if (!option.value) continue;
          const variant = this.findVariant(pick.colour, option.value);
          option.disabled = !variant || !variant.available;
        }
      }
      if (pick.extra.colour && this.offer === 'box3') {
        for (const option of Array.from(pick.extra.colour.options)) {
          const variant = this.findVariant(option.value, this.size);
          option.disabled = this.size ? !variant || !variant.available : false;
        }
      }
    }

    const { items, problem, focus, extra } = this.resolve();
    const ready = items.length > 0;
    const multi = this.qty > 1;
    const price = multi ? figures?.total : single?.priceMoney;

    let label;
    if (ready) label = multi ? `Add ${this.qty} to bag — ${price}` : `Add to bag — ${price}`;
    else if (focus === 'extra') label = `Choose a size for ${this.noun} ${(extra ?? 0) + 2}`;
    else label = 'Choose a size';
    if (this.addButton) this.addButton.textContent = label;

    const barAdd = this.bar?.querySelector('[data-bar-add]');
    const barPrice = this.bar?.querySelector('[data-bar-price]');
    if (barAdd) barAdd.textContent = ready ? (multi ? `Add ${this.qty} to bag` : 'Add to bag') : 'Choose a size';
    if (barPrice && price) barPrice.textContent = price;

    this.#say(problem || this.#selectionText(), problem ? 'error' : '');
  }

  #selectionText() {
    if (this.needsSize && !this.size) return '';
    const describe = (colour, size) => [colour, this.needsSize ? size : null].filter(Boolean).join(' · ');
    const parts = [describe(this.colourButtons.length ? this.colour : null, this.size)];
    for (const pick of this.#extraPicks()) {
      parts.push(describe(this.colourButtons.length ? pick.colour : null, pick.size || 'choose a size'));
    }
    return parts.filter(Boolean).join(' + ');
  }

  #say(message, state) {
    if (!this.summary) return;
    this.summary.textContent = message;
    if (state) this.summary.dataset.state = state;
    else delete this.summary.dataset.state;
  }

  #openFitGuide() {
    const details = document.getElementById('lyne-fit-guide');
    if (details) details.open = true;
    details?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    details?.querySelector('summary')?.focus();
  }

  /** Reveal the mobile bar only while the real button is out of view, and reserve its height. */
  #watchStickyBar() {
    if (!this.bar || !this.addButton) return;
    const observer = new IntersectionObserver(
      (entries) => {
        this.bar.hidden = entries[0].isIntersecting;
        this.#reserveBarSpace();
      },
      { threshold: 0 }
    );
    observer.observe(this.addButton);
    window.addEventListener('resize', () => this.#reserveBarSpace(), { passive: true });
  }

  #reserveBarSpace() {
    if (!this.bar) return;
    const visible = !this.bar.hidden && getComputedStyle(this.bar).display !== 'none';
    const height = visible ? Math.ceil(this.bar.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--lyne-buy-bar-space', `${height}px`);
  }

  async fetchCart() {
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems && typeof cartItems.fetchCartData === 'function') {
      await customElements.whenDefined('cart-items-component');
      return cartItems.fetchCartData();
    }
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    return response.json();
  }

  async submit() {
    const { items, problem, focus, extra } = this.resolve();

    if (!items.length) {
      if (focus === 'extra') {
        const row = this.#extraPicks()[extra ?? 0]?.extra;
        this.#say(problem || `Please choose a size for ${this.noun} ${(extra ?? 0) + 2}.`, 'error');
        (row?.size || row?.colour)?.focus();
        row?.row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      this.#say(problem || 'Please choose a size.', 'error');
      const target = this.sizeButtons.find((button) => !button.disabled) || this.sizeButtons[0];
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target?.focus();
      return;
    }

    if (this.addButton.dataset.busy === 'true') return;
    this.addButton.dataset.busy = 'true';
    this.addButton.disabled = true;

    const quantity = items.reduce((total, item) => total + item.quantity, 0);
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => element instanceof HTMLElement && element.dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const result = await response.json();
      const cart = await this.fetchCart();
      const detail = {
        items: cart.items,
        source: 'product-form-component',
        sourceId: this.id,
        itemCount: quantity,
        productId: this.dataset.productId,
      };

      if (result.status) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...detail, didError: true },
        });
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        this.#say(result.description || result.message || 'That could not be added. Please try again.', 'error');
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { ...detail, sections: result.sections, didError: false },
      });
      this.#say(quantity > 1 ? `${quantity} ${this.nouns} added to your bag.` : 'Added to your bag.', '');
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
      );
      this.#say('Something went wrong. Please try again.', 'error');
    } finally {
      this.addButton.dataset.busy = 'false';
      this.addButton.disabled = false;
      // Keep the success message on screen; refresh() would overwrite it with the selection.
      const message = this.summary?.textContent;
      const state = this.summary?.dataset.state;
      this.refresh();
      if (message) this.#say(message, state || '');
    }
  }
}

if (!customElements.get('lyne-buy')) {
  customElements.define('lyne-buy', LyneBuy);
}
